import useRequest from "../../../components/hook/use-req";
import { SegmentApiRecord } from "./types";

// Confirmed real shape (2026-07-28) for GET /segments/.
export interface SegmentsListResponse {
  total: number;
  pages: number;
  page: number;
  limit: number;
  data: SegmentApiRecord[];
  search?: string;
}

// Per the Segments API doc (2026-07-29): every request needs both headers.
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  "X-AUTH-TYPE": "jwt",
});

// GET /segments/ — paginated list, params: { page, limit, search }.
export const useListSegments = () => useRequest("/segments/", "GET", authHeaders());

// POST /segments/ — { name, source_scope }. The rule tree is set afterwards
// via the /segments/{id}/rules update once the segment (and its id) exists.
export const useCreateSegment = () => useRequest("/segments/", "POST", authHeaders());

// GET /segments/{id} — full segment details: the saved rule_tree, live
// counts, and a paginated page of its real matched contacts (results).
// Call as getSegment(undefined, { page, limit }, id).
export const useGetSegment = () => useRequest("/segments", "GET", authHeaders());

// PUT /segments/{id}/rules — full replace of a segment's rule_tree (send the
// whole tree every time, this is not a partial patch). Call as
// updateRules({ rule_tree }, undefined, `${id}/rules`).
export const useUpdateSegmentRules = () => useRequest("/segments", "PUT", authHeaders());

// GET /segments/counts?rule_tree=<url-encoded JSON>&source_scope=all|crm —
// counts for BOTH "all" and "crm" scope side by side (the response includes
// both regardless of source_scope), for the "All Connected Data"/"ConvoCRM
// Data" scope cards. Omit rule_tree for the unfiltered baseline totals.
export const useSegmentCounts = () => useRequest("/segments/counts", "GET", authHeaders());

// GET /segments/attributes?source_scope=... — attribute catalog for the rule
// picker, grouped by section ("Common Fields", "ConvoCRM Attributes", ...),
// each field carrying its own type + valid operators.
export const useSegmentAttributes = () => useRequest("/segments/attributes", "GET", authHeaders());

// GET /segments/events — event catalog for the "Choose an Event" picker,
// grouped into two sections: "Broadcast Events" (a fixed 10-name catalog of
// delivery outcomes) and "Custom Events" (built-in + org-registered events).
export const useSegmentEvents = () => useRequest("/segments/events", "GET", authHeaders());

// POST /segments/campaign/{campaignId} — { source_scope, metric, negate,
// occurred_in_days, channel } — creates a segment from one of a broadcast's
// per-metric audiences (e.g. "delivered", "opened") in one call (no separate
// name step). `negate: true` means "did NOT hit this metric";
// `occurred_in_days` narrows to only recent occurrences (omit/null for
// "ever"); `channel` (e.g. "whatsapp") prefills the returned rule_tree's
// Broadcast Delivered/Clicked condition with that broadcast's channel via
// attribute_path "broadcast_channel" (see segment-builder.tsx's "Channel"
// narrowing UI, which also lets it be changed afterwards). Response is
// { data: { name, rule_tree, ... } } — the Broadcast Report's "Add to
// Segment" buttons hand this straight to the segment builder to review/save.
// Call as createFromCampaign({ source_scope, metric, negate, occurred_in_days, channel }, undefined, campaignId).
export const useCreateSegmentFromCampaign = () => useRequest("/segments/campaign", "POST", authHeaders());
